import React from 'react';
import { Helmet } from 'react-helmet-async';
import seo from './routes.json';

export interface SeoProps {
  title: string;
  description: string;
  path?: string;
  type?: 'website' | 'article';
  ogImage?: string;
  ogImageAlt?: string;
  noindex?: boolean;
  jsonLd?: object | object[];
}

// Everything here is derived from props rather than from window.location: Helmet
// reconciles its [data-rh] tags by isEqualNode on hydration, and a value that only
// exists in the browser would make every prerendered head flicker on load.
const Seo: React.FC<SeoProps> = ({
  title,
  description,
  path,
  type = 'website',
  ogImage,
  ogImageAlt,
  noindex,
  jsonLd,
}) => {
  const url = path ? `${seo.siteUrl}${path === '/' ? '/' : path.replace(/\/$/, '')}` : undefined;
  const card = seo.routes.find((route) => route.path === path)?.og ?? 'default';
  const relative = ogImage ?? `/og/${card}.png`;
  const image = relative.startsWith('http')
    ? relative
    : `${seo.siteUrl}${relative}?v=${seo.ogVersion}`;
  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {noindex ? <meta name="robots" content="noindex" /> : null}
      {url ? <link rel="canonical" href={url} /> : null}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Kult.io" />
      <meta property="og:locale" content="en_US" />
      {url ? <meta property="og:url" content={url} /> : null}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={ogImageAlt ?? title} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      {blocks.map((block, index) => (
        // The escape closes the </script> injection route through any string that
        // reaches a JSON-LD value (a story title today, an API-fed one tomorrow).
        <script key={index} type="application/ld+json">
          {JSON.stringify(block).replace(/</g, '\\u003c')}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
